import "./FlexibleContent.scss"
import React, { forwardRef } from "react"
import cx from "classnames"

import { Flex, FlexItem } from "components/Flex"

import Button from "components/Button/Button"
import ClampedTooltip from "components/Clamped/ClampedTooltip"
import Highlighter, { IHighlighterProps } from "components/Highlighter/Highlighter"
import { Clamp } from "@/types"

interface IFlexibleContentTextProps {
    clamp?: Clamp
    ariaLabel?: string
    children?: React.ReactNode
    className?: string
    testId?: string
}

const FlexibleContentText = forwardRef<HTMLSpanElement, IFlexibleContentTextProps>(({ children, clamp, ariaLabel, className, testId }, ref) => {
    return (
        <span
            className={cx([
                `word-break-break-${clamp && clamp === 1 ? 'all' : 'word'}`, // maximize visible letter when displaying single line
                clamp ? `clamp-${clamp}` : '',
                className,
            ])}
            ref={ref}
            aria-label={ariaLabel}
            data-testid={testId}>
            {children}
        </span>
    )
})
FlexibleContentText.displayName = "FlexibleContentText"

interface IFlexibleContentProps {
    text?: string
    highlight?: IHighlighterProps["highlight"]
    insertAfter?: React.ReactNode
    insertBefore?: React.ReactNode
    onClick?(): void
    clamp?: Clamp
    clampedTooltip?: boolean
    className?: string
    testId?: string
}

const FlexibleContent = forwardRef<HTMLSpanElement, IFlexibleContentProps>(({
    text,
    highlight,
    insertAfter,
    insertBefore,
    onClick,
    clamp = 1,
    clampedTooltip = true,
    className = "",
    testId,
}, ref) => {
    function handleClick(event: MouseEvent) {
        if (typeof onClick !== "function") return

        event.preventDefault()
        event.stopPropagation()

        onClick()
    }

    const highlightedText = <Highlighter text={text || ""} highlight={highlight || ""} />

    function renderText(params?: { addAriaLabel?: boolean }) {
        if (!clampedTooltip) {
            return (
                <FlexibleContentText
                    clamp={clamp}
                    ariaLabel={params?.addAriaLabel ? text : undefined}
                    className={className}
                    testId={testId}
                    ref={ref}>
                    {highlightedText}
                </FlexibleContentText>
            )
        }
        return (
            <ClampedTooltip
                originalText={text}
                clamp={clamp}>
                <FlexibleContentText className={className} testId={testId} ref={ref}>{highlightedText}</FlexibleContentText>
            </ClampedTooltip>
        )
    }

    function renderContent(params?: { addAriaLabel?: boolean }) {
        return (
            <Flex aria-label={params?.addAriaLabel ? text : undefined}
                alignItems="center">
                {!!insertBefore &&
                    <FlexItem className="mr-1">
                        {insertBefore}
                    </FlexItem>
                }
                <FlexItem>
                    {renderText()}
                </FlexItem>
                {!!insertAfter &&
                    <FlexItem className="ml-1">
                        {insertAfter}
                    </FlexItem>
                }
            </Flex>
        )
    }

    if (!text) return null

    if (!onClick && !insertAfter && !insertBefore) {
        return renderText({ addAriaLabel: true })
    }

    if (typeof onClick !== "function") {
        return renderContent({ addAriaLabel: true })
    }

    return (
        <Button
            onClick={handleClick}
            label={renderContent()}
            theme="link"
            userSelect="text"
            ariaLabel={text}
            className={`font-weight-inherit ${className}`} />
    )
})

FlexibleContent.displayName = "FlexibleContent"
export default FlexibleContent
